import { UnifiedListLayout } from '@/components/layout/unified-list-layout';
import { IntroCard } from '@/components/shared/IntroCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { useTimer } from '@/contexts/timer-context';
import { useChapters } from '@/hooks/useChapters';

import { useStoriesByProject } from '@/hooks/useStories';
import { cn } from "@/lib/utils";
import {
  useChapterStats,
  useCreateGoal,
  useDeleteGoal,
  useGoals,
  useProjectProgressSummary,
  useUpdateGoalDeadline,
  useUpdateGoalProgress,
} from '@/services/query/progress-tracker-queries';
import { format } from 'date-fns';
import { motion } from "framer-motion";
import {
  ChevronLeft,
  FileText,
  Flame,
  LayoutDashboard,
  Play,
  PlusCircle,
  Target,
  Trophy
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ActiveSessionCard from './components/active-session-card';
import ActivityHeatmap from './components/activity-heatmap';
import GoalCompletionDialog from './components/goal-completion-dialog';
import GoalDeleteDialog from './components/goal-delete-dialog';
import GoalEditDialog from './components/goal-edit-dialog';
import GoalForm from './components/goal-form';
import GoalsTab from './components/goals-tab';
import WordCountTab from './components/word-count-tab';
import { WritingGoal } from './types';


// Internal Dashboard Card Component (Same as OverviewPage for consistency)
const DashboardCard = ({
  title,
  count,
  action,
  children,
  className,
  delay = 0,
  icon
}: {
  title: string,
  count?: number | string,
  action?: React.ReactNode,
  children: React.ReactNode,
  className?: string,
  delay?: number,
  icon?: React.ReactNode
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: "easeOut" }}
    className={cn("h-full", className)}
  >
    <Card className="bg-card border-border shadow-sm hover:shadow-md hover:border-border transition-all duration-200 overflow-hidden flex flex-col h-full rounded-2xl group/card">
      <div className="flex items-center justify-between p-6 border-b border-border bg-transparent transition-colors duration-200">
        <div className="flex items-center gap-2.5">
          {icon && <div className="text-muted-foreground group-hover/card:text-primary transition-colors">{icon}</div>}
          <h3 className="text-base font-semibold text-card-foreground tracking-tight">{title}</h3>
          {count !== undefined && (
            <Badge variant="secondary" className="px-1.5 min-w-[20px] h-5 text-[10px] flex items-center justify-center bg-muted text-muted-foreground border-border rounded-md">
              {count}
            </Badge>
          )}
        </div>
        {action}
      </div>
      <CardContent className="p-0 flex-1 relative bg-transparent">
        {children}
      </CardContent>
    </Card>
  </motion.div>
);

export default function ProgressTrackerPage() {
  const { projectId, storyId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');

  // Timer Context
  const { startSession, isTimerRunning } = useTimer();

  // State
  const [activeSessionRefresh, setActiveSessionRefresh] = useState(0);

  // Dialog States
  const [creatingGoal, setCreatingGoal] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalToComplete, setGoalToComplete] = useState<WritingGoal | null>(null);
  const [goalToEdit, setGoalToEdit] = useState<WritingGoal | null>(null);
  const [goalToDelete, setGoalToDelete] = useState<WritingGoal | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processingGoalId, setProcessingGoalId] = useState<string>('');

  // Queries
  const { data: storiesData } = useStoriesByProject(projectId);
  const { data: chaptersData } = useChapters(storyId);

  const { data: goalsData = [] } = useGoals(projectId, { enabled: activeTab === 'goals' });
  const { data: progressSummary } = useProjectProgressSummary(projectId);

  // Derived state from summary
  const streak = progressSummary?.streaks.currentStreak || 0;
  const longestStreak = progressSummary?.streaks.longestStreak || 0;
  const writingStats = progressSummary?.overall_stats;

  const { data: chapterStats = [], isLoading: isChapterStatsLoading, refetch: refetchChapterStats } = useChapterStats(projectId || '');

  // Intro Card State
  const [isIntroVisible, setIsIntroVisible] = useState(true);

  useEffect(() => {
    if (projectId) {
      const isClosed = localStorage.getItem(`progress_tracker_intro_closed_${projectId}`);
      setIsIntroVisible(isClosed !== 'true');
    }
  }, [projectId]);

  const dismissIntro = () => {
    if (projectId) {
      localStorage.setItem(`progress_tracker_intro_closed_${projectId}`, 'true');
      setIsIntroVisible(false);
    }
  };

  // Mutations

  // Mutations
  const createGoalMutation = useCreateGoal();
  const updateGoalProgressMutation = useUpdateGoalProgress();
  const updateGoalDeadlineMutation = useUpdateGoalDeadline();
  const deleteGoalMutation = useDeleteGoal();



  // Process Goals
  // Merge active goals from summary with full goals list if available
  const goals = React.useMemo(() => goalsData || [], [goalsData]);
  const activeGoals = React.useMemo(() => {
    if (progressSummary?.activeGoals) {
      return progressSummary.activeGoals;
    }
    return goals.filter(g => !g.completed);
  }, [progressSummary, goals]);

  // Process Sessions
  const sessions = React.useMemo(() => {
    // Use recent sessions from summary
    if (progressSummary?.recentSessions) {
      return progressSummary.recentSessions.map(session => ({
        id: session.id,
        story_id: session.story_id,
        story_name: session.story_name || storiesData?.find(s => parseInt(s.id) === session.story_id)?.name || 'Unknown Story',
        chapter_id: session.chapter_id,
        chapter_name: session.chapter_name || chaptersData?.find(c => c.id === session.chapter_id)?.title || '',
        start_time: new Date(session.start_time),
        end_time: new Date(session.end_time),
        duration: Math.round(session.duration / 60),
        created_at: new Date(session.created_at),
        updated_at: new Date(session.updated_at)
      }));
    }
    return [];
  }, [progressSummary, storiesData, chaptersData]);

  // Effects
  // Effects
  useEffect(() => {
    // Refresh handler if needed
  }, [projectId, storyId, activeSessionRefresh]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['overview', 'goals', 'analytics'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location]);



  // Handlers
  const handleUpdateProgress = (goal: WritingGoal) => {
    setGoalToComplete(goal);
    setCompletionDialogOpen(true);
  };

  const handleEditGoal = (goal: WritingGoal) => {
    setGoalToEdit(goal);
    setEditDialogOpen(true);
  };

  const handleDeleteGoal = (goal: WritingGoal) => {
    setGoalToDelete(goal);
    setDeleteDialogOpen(true);
  };

  const handleSessionEnd = () => {
    setActiveSessionRefresh(prev => prev + 1);
  };

  const handleStartSession = () => {
    if (projectId) {
      startSession(projectId);
    }
  };

  const formatTimeSpent = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const confirmGoalCompletion = async () => {
    if (!goalToComplete) return;
    setProcessingGoalId(goalToComplete.id);
    const newCompletionState = !goalToComplete.completed;
    try {
      await updateGoalProgressMutation.mutateAsync({
        id: goalToComplete.id,
        completed: newCompletionState,
        projectId: projectId
      });
      toast({
        title: newCompletionState ? "Goal completed" : "Goal active",
        description: newCompletionState ? "Great job!" : "Goal marked as incomplete."
      });
      setCompletionDialogOpen(false);
      setGoalToComplete(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update goal", variant: "destructive" });
    } finally {
      setProcessingGoalId("");
    }
  };

  const confirmGoalEdit = async (newDeadline: string) => {
    if (!goalToEdit) return;
    setProcessingGoalId(goalToEdit.id);
    try {
      await updateGoalDeadlineMutation.mutateAsync({
        id: goalToEdit.id,
        deadline: newDeadline,
        projectId: projectId
      });
      toast({ title: "Goal updated", description: "Deadline updated successfully." });
      setEditDialogOpen(false);
      setGoalToEdit(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update goal", variant: "destructive" });
    } finally {
      setProcessingGoalId("");
    }
  };

  const confirmGoalDelete = async () => {
    if (!goalToDelete) return;
    setProcessingGoalId(goalToDelete.id);
    try {
      await deleteGoalMutation.mutateAsync({ id: goalToDelete.id, projectId: projectId });
      toast({ title: "Goal deleted", description: "Goal removed successfully." });
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
    } catch (error) {
      toast({ title: "Error cancel", description: "Failed to delete goal", variant: "destructive" });
    } finally {
      setProcessingGoalId("");
    }
  };

  return (
    <UnifiedListLayout
      toolbar={
        <div className="flex items-center justify-end w-full">
          {!isTimerRunning && (
            <Button onClick={handleStartSession} className="gap-2 shadow-sm">
              <Play className="h-4 w-4 fill-current" /> Start Session
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-8 pb-20">

        {/* Intro Card */}
        {
          isIntroVisible && (
            <IntroCard
              title="Monitor your Productivity"
              description="Track your writing habits, set word count goals, and visualize your progress over time. Consistency is key to finishing your story."
              onClose={dismissIntro}
              className="mb-8"
              actions={
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Writing Sprints</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Use the 'Start Session' button to track focused writing sprints and measure your output velocity.
                    </p>
                  </div>
                  {!isTimerRunning && (
                    <Button
                      variant="default"
                      className="w-full justify-start text-xs h-9"
                      onClick={handleStartSession}
                    >
                      <Play className="h-3 w-3 mr-2" />
                      Start Session
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start text-xs h-9"
                    onClick={() => setGoalDialogOpen(true)}
                  >
                    <Target className="h-3 w-3 mr-2" />
                    Set New Goal
                  </Button>
                </div>
              }
            />
          )
        }

        <ActiveSessionCard onSessionEnd={handleSessionEnd} />

        <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-transparent p-0 gap-10 border-b border-border w-full justify-start rounded-none h-auto">
            <TabsTrigger
              value="overview"
              className="px-0 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground hover:text-foreground transition-all"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="goals"
              className="px-0 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground hover:text-foreground transition-all"
            >
              Goals
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="px-0 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground hover:text-foreground transition-all"
            >
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-8 animate-in fade-in-50 duration-500">
            {/* Top Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

              {/* Card 1: Writing Streak */}
              <DashboardCard
                title="Consistency"
                icon={<Flame className="h-5 w-5 text-foreground" />}
                delay={0.1}
                className="min-h-[200px]"
              >
                <div className="flex flex-col h-full p-6 justify-between">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold tracking-tight">{streak}</span>
                      <span className="text-muted-foreground font-medium">day streak</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex justify-between items-center text-sm -mx-6 px-6">
                    <span className="text-muted-foreground">Longest Streak</span>
                    <div className="flex items-center gap-1.5 font-medium">
                      <Trophy className="h-3.5 w-3.5 text-foreground" />
                      {longestStreak} days
                    </div>
                  </div>
                </div>
              </DashboardCard>

              {/* Card 2: Total Output */}
              <DashboardCard
                title="Total Output"
                icon={<FileText className="h-5 w-5 text-foreground" />}
                delay={0.2}
                className="min-h-[200px]"
              >
                <div className="flex flex-col h-full p-6 justify-between">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold tracking-tight">
                        {formatTimeSpent(writingStats?.totalTimeSpent || 0).split(' ')[0]}
                      </span>
                      <span className="text-muted-foreground font-medium">
                        {formatTimeSpent(writingStats?.totalTimeSpent || 0).split(' ').slice(1).join(' ') || 'time spent'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex justify-between items-center text-sm -mx-6 px-6">
                    <span className="text-muted-foreground">Total Sessions</span>
                    <div className="flex items-center gap-1.5 font-medium">
                      <LayoutDashboard className="h-3.5 w-3.5 text-foreground" />
                      {writingStats?.totalSessions || 0} sessions
                    </div>
                  </div>
                </div>
              </DashboardCard>

              {/* Card 3: Quick Goal or Active Session CTA */}
              <DashboardCard
                title="Active Goals"
                count={activeGoals.length}
                icon={<Target className="h-5 w-5 text-foreground" />}
                delay={0.3}
                className="min-h-[200px]"
                action={
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('goals')} className="h-6 w-6 p-0 rounded-full">
                    <ChevronLeft className="h-4 w-4 rotate-180" />
                  </Button>
                }
              >
                {activeGoals.length > 0 ? (
                  <div className="flex flex-col h-full">
                    {activeGoals.slice(0, 3).map((goal) => (
                      <div key={goal.id} className="p-4 py-3 border-b last:border-0 border-border hover:bg-muted/5 transition-colors flex items-center gap-3">
                        <div className={cn("h-2 w-2 rounded-full flex-shrink-0", new Date(goal.deadline) < new Date() ? "bg-red-500" : "bg-primary")} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate leading-none mb-1">{goal.name}</p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Due {format(new Date(goal.deadline), 'MMM d')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                    <p className="text-sm text-muted-foreground mb-4">No active goals.</p>
                    <Button variant="outline" size="sm" onClick={() => setGoalDialogOpen(true)}>
                      <PlusCircle className="mr-2 h-3.5 w-3.5" /> Set Goal
                    </Button>
                  </div>
                )}
              </DashboardCard>
            </div>

            {/* Heatmap Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="h-[400px] bg-card/50 backdrop-blur-sm border-border shadow-sm flex flex-col">
                  {/* Internal header used inside ActivityHeatmap now */}
                  <CardContent className="pt-6 h-full flex flex-col">
                    <ActivityHeatmap projectId={projectId} className="flex-1 min-h-0" />
                  </CardContent>
                </Card>
              </div>

              {/* Recent Sessions List */}
              <div className="lg:col-span-1">
                <Card className="h-[400px] bg-card/50 backdrop-blur-sm border-border shadow-sm flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">Recent Sessions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 overflow-auto scrollbar-hide">
                    {sessions.length > 0 ? (
                      <div className="divide-y divide-border">
                        {sessions.slice(0, 10).map(session => (
                          <div key={session.id} className="p-4 hover:bg-muted/5 transition-colors">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-sm truncate max-w-[150px]">{session.story_name}</span>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{format(session.start_time, 'MMM d')}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs text-muted-foreground">
                              <span>{session.chapter_name || "Untitled Chapter"}</span>
                              <Badge variant="secondary" className="h-5 px-1.5 font-normal">{session.duration}m</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-sm text-muted-foreground">
                        No recent sessions.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* GOALS TAB */}
          <TabsContent value="goals" className="animate-in fade-in-50 duration-500">
            <GoalsTab
              goals={goals}
              creatingGoal={creatingGoal}
              setGoalDialogOpen={setGoalDialogOpen}
              handleUpdateProgress={handleUpdateProgress}
              handleEditGoal={handleEditGoal}
              handleDeleteGoal={handleDeleteGoal}
            />
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics" className="animate-in fade-in-50 duration-500">
            <WordCountTab
              projectId={projectId || ''}
              chapterStats={chapterStats}
              isLoading={isChapterStatsLoading}
              onRefresh={refetchChapterStats}
              availableStories={storiesData ? storiesData.map(s => ({ id: parseInt(s.id), name: s.name })) : []}
            />
          </TabsContent>
        </Tabs >

        {/* Dialogs */}
        < GoalForm
          open={goalDialogOpen}
          onOpenChange={setGoalDialogOpen}
          onSubmit={async (data) => {
            setCreatingGoal(true);
            try {
              await createGoalMutation.mutateAsync({
                name: data.name,
                deadline: data.deadline,
                story_id: data.storyId || null,
                projectId: data.projectId || parseInt(projectId || '0')
              });
              setGoalDialogOpen(false);
            } catch (err) {
              toast({ title: "Error", description: "Failed to create goal", variant: "destructive" });
            } finally {
              setCreatingGoal(false);
            }
          }
          }
          stories={storiesData || []}
          loading={creatingGoal}
        // projectId removed as it is now handled internally via useParams in GoalForm
        />

        <GoalCompletionDialog
          open={completionDialogOpen}
          onOpenChange={setCompletionDialogOpen}
          onConfirm={confirmGoalCompletion}
          goalName={goalToComplete?.name || ''}
          isLoading={!!processingGoalId}
        />

        <GoalEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onConfirm={confirmGoalEdit}
          currentDeadline={goalToEdit?.deadline || ''}
          goalName={goalToEdit?.name || ''}
          isLoading={!!processingGoalId}
        />

        <GoalDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmGoalDelete}
          goalName={goalToDelete?.name || ''}
          isLoading={!!processingGoalId}
        />

      </div >
    </UnifiedListLayout >
  );
}